import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Box, CircularProgress, Alert, Typography } from '@mui/material';
import { useAppSelector } from '../store/hooks';
import { supabase } from '../api/supabase';

interface AdminProtectedRouteProps {
    children: React.ReactNode;
}

/**
 * AdminProtectedRoute component that protects admin-only routes
 *
 * Features:
 * - Checks user authentication status
 * - Verifies admin role/permissions
 * - Shows loading state while checking permissions
 * - Redirects non-admin users appropriately
 * - Displays error messages for unauthorized access
 */
const AdminProtectedRoute: React.FC<AdminProtectedRouteProps> = ({ children }) => {
    const { user } = useAppSelector((state) => state.auth);
    const location = useLocation();
    const [isAdmin, setIsAdmin] = useState<boolean | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        checkAdminAccess();
    }, [user]);

    const checkAdminAccess = async () => {
        try {
            setLoading(true);
            setError(null);

            // Check if user is authenticated
            if (!user) {
                setIsAdmin(false);
                setLoading(false);
                return;
            }

            // Method 1: Check JWT claims for admin role
            const { data: { session } } = await supabase.auth.getSession();
            if (session?.access_token) {
                try {
                    // Decode JWT to check for admin role
                    const payload = JSON.parse(atob(session.access_token.split('.')[1]));
                    if (payload.user_role === 'admin' || payload.role === 'admin') {
                        setIsAdmin(true);
                        setLoading(false);
                        return;
                    }
                } catch (jwtError) {
    // Console statement removed
  }
            }

            // Method 2: Check user metadata for admin role
            if (user.user_metadata?.role === 'admin' || user.app_metadata?.role === 'admin') {
                setIsAdmin(true);
                setLoading(false);
                return;
            }

            // Method 3: Check profiles table for admin status
            try {
                const { data: profile, error: profileError } = await supabase
                    .from('profiles')
                    .select('id, email, is_admin')
                    .eq('id', user.id)
                    .single();

                if (profileError) {
                    throw profileError;
                }

                // Check database is_admin column first
                if (profile?.is_admin === true) {
                    setIsAdmin(true);
                    setLoading(false);
                    return;
                }

                // Fallback: Check email whitelist (for initial setup)
                const adminEmails = [
                    // Add your admin email addresses here for initial setup
                    'admin@poptheballoonapp.com',
                    'mays@poptheballoonapp.com',
                    'mayswill3@hotmail.co.uk',
                    // Add more admin emails as needed
                ];

                const isAdminUser = adminEmails.includes(user.email || '');
                setIsAdmin(isAdminUser);

                if (!isAdminUser) {
    // Console statement removed
  }

            } catch (profileError) {
                setError('Unable to verify admin permissions. Please try again.');
                setIsAdmin(false);
            }

        } catch (error: any) {
            setError('Failed to verify admin permissions.');
            setIsAdmin(false);
        } finally {
            setLoading(false);
        }
    };

    // Show loading spinner while checking permissions
    if (loading) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: '100vh',
                    gap: 2
                }}
            >
                <CircularProgress size={48} />
                <Typography variant="body1" color="text.secondary">
                    Verifying admin permissions...
                </Typography>
            </Box>
        );
    }

    // Show error if permission check failed
    if (error) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: '100vh',
                    p: 3,
                    maxWidth: 500,
                    mx: 'auto'
                }}
            >
                <Alert severity="error" sx={{ width: '100%', mb: 2 }}>
                    <Typography variant="h6" gutterBottom>
                        Access Verification Failed
                    </Typography>
                    <Typography variant="body2">
                        {error}
                    </Typography>
                </Alert>
                <Typography variant="body2" color="text.secondary" textAlign="center">
                    If you believe this is an error, please contact support or try refreshing the page.
                </Typography>
            </Box>
        );
    }

    // Redirect to login if not authenticated
    if (!user) {
        return <Navigate to="/login" state={{ from: location }} replace />;
    }

    // Show unauthorized message if not admin
    if (isAdmin === false) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: '100vh',
                    p: 3,
                    maxWidth: 500,
                    mx: 'auto'
                }}
            >
                <Alert severity="warning" sx={{ width: '100%', mb: 2 }}>
                    <Typography variant="h6" gutterBottom>
                        Access Denied
                    </Typography>
                    <Typography variant="body2">
                        You don't have permission to access the admin dashboard.
                        This area is restricted to authorized administrators only.
                    </Typography>
                </Alert>
                <Typography variant="body2" color="text.secondary" textAlign="center">
                    If you need admin access, please contact your system administrator.
                </Typography>
            </Box>
        );
    }

    // User is authenticated and is admin - render protected content
    return <>{children}</>;
};

export default AdminProtectedRoute;